// src/sim/items.ts — Eşya/silah sistemi (WP4): doğma, alma, düşürme, fırlatma, uçan mermi isabeti, dayanıklılık, yerdeki eşya fiziği.
// ARCHITECTURE §3 adım 8 (`systems.items.step`), §4.4 son paragraf (mermi → `systems.applyKnockback`), §12 WP4; DESIGN §9, §4.2, §2 B.
// three import ETMEZ; rastgelelik yalnız hashNoise(seed, tick, tuz); sıcak yolda tahsis yok (scratch vektörler, havuz slotları).
// Durum tamamen `Item` / `Fighter` alanlarında tutulur (modül düzeyinde sayaç yok → world.hash() ve determinizm bozulmaz):
//   - Doğma zamanlayıcısı `tick`ten türetilir: tick ≥ firstSpawn ∧ (tick − firstSpawn) % interval == 0 (dünya kuruluşu = maç başı).
//   - Bekleyen fırlatma: item.state 'held' ∧ item.thrower ≥ 0; bırakma tick'i item.pickableAt'ta, bırakma hızı item.vel'de saklı.
//   - "Yere inmiş" işareti: state 'ground' ∧ vel.y == 0 (havadayken ilk adımdan sonra vel.y < 0).
import { Ev, FState } from '../core/types.ts';
import type { Fighter, Item, ItemApi, PropId, SimWorld, SpawnableKind, Vec3, WeaponKind } from '../core/types.ts';
import { v3, v3set, fwdX, fwdZ, leftX, leftZ, yawOf } from '../core/vec3.ts';
import { clamp, degToRad, hashNoise, lerp } from '../core/math.ts';
import { HURT_R, HURT_Y0, HURT_Y1, LYING_HURT_HALF, LYING_HURT_Y, MAX_ITEMS, NO_ID, NO_TICK, TICK_DT, TICK_HZ } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { MOVE_ID, moveById } from '../config/moves.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

// ───────────────────────────── Yerel sabitler (tuning'de karşılığı yok → needsWP0) ─────────────────────────────
/** Fırlatma bırakma noktası: el ≈ ileri 0.5 u, yukarı 1.3 u (ANIMATION §6.4: Throw'da el y 1.38→0.92 süpürür). y 1.3 + g −8 ile
 *  yere iniş 0.57 s → 16·0.57 + 0.5 ≈ 9.6 u (DESIGN §9 "menzil ≈ 10 u"). needsWP0: ITEMS.throwReleaseFwd / throwReleaseUp. */
const THROW_RELEASE_FWD = 0.5;
const THROW_RELEASE_UP = 1.3;
/** Düşürülen prop elden çıkar: yukarı 1.2 u; kalkan sol elde (sol 0.3 u). needsWP0: ITEMS.dropOriginUp / dropOriginSide. */
const DROP_ORIGIN_UP = 1.2;
const DROP_ORIGIN_SIDE = 0.3;
/** Dual doğuşunda iki hançer aynı noktada üst üste kalmasın: ±0.15 u yan ofset. needsWP0: ITEMS.dualSpawnSpread. */
const DUAL_SPAWN_SPREAD = 0.15;
/** Doğma noktası "dolu" sayma yarıçapı (üstünde yerde/uçan eşya varsa o nokta seçilmez). needsWP0: ITEMS.spawnPointClearR. */
const SPAWN_POINT_CLEAR_R = 1.5;
/** Uçan silah isabetinde hitstop (ağır darbe 5 t): DESIGN §9 "bloklanır" → blok/parry/zırh hits.ts resolveCore'da (applyKnockback);
 *  guard maliyeti orada varsayılan hafif (−12) — ağır (−30) için ApplyKnockbackFn'e guardCost parametresi gerekir (needsWP0). */
const THROW_HITSTOP = T.HITSTOP.heavy;
/** Silah fırlatma toplam süresi: bırakma 19 t + G2 Throw'un active+recover'ı (1 + 15) = 35 t (ANIMATION §2.2 WeaponThrow = Throw tS 1.5, aynı klip). */
const G2 = moveById(MOVE_ID.G2);
const WEAPON_THROW_TICKS = T.ITEMS.throwReleaseTick + G2.active + G2.recover;

/** hashNoise tuzları (aynı tick'te farklı kararlar bağımsız görünsün). */
const SALT_POINT = 101, SALT_KIND = 202, SALT_PROP = 303, SALT_DROP_UP = 404, SALT_DROP_BACK = 505;

// ───────────────────────────── Havuz ─────────────────────────────
/** Tek eşya kaydı; tüm alanlar her zaman aynı tip (hidden class). Başlangıçta 'dead'. */
export function createItem(id: number): Item {
  return {
    id, kind: '1h', propId: 'sword_1handed', state: 'dead',
    pos: v3(), vel: v3(), yaw: 0, spin: 0,
    holder: NO_ID, thrower: NO_ID, spawnedAt: NO_TICK, pickableAt: NO_TICK, despawnAt: NO_TICK,
    hits: 0, breakAt: NO_TICK, hitMask: 0, length: 0, holderOffsetY: 0,
  };
}
/** world.items için havuz (WP2 world.ts / WP9 kurulumda çağırır). spawn() dolu havuzda `items.length < MAX_ITEMS` ise bir slot ekler (sınırlı). */
export function createItemPool(n: number = MAX_ITEMS): Item[] {
  const out: Item[] = [];
  for (let i = 0; i < n; i++) out.push(createItem(i));
  return out;
}

function isWeaponKind(kind: SpawnableKind): boolean { return kind !== 'shield'; }
function is2H(item: Item): boolean { return item.kind === '2h'; }
/** Eşya türü → dövüşçü silah sınıfı (tek hançer '1h'; dual yalnız iki hançer birlikte alınınca). */
function weaponKindOf(kind: SpawnableKind): WeaponKind {
  return kind === 'dual' ? '1h' : kind === 'shield' ? 'unarmed' : kind;
}

function fighterById(world: SimWorld, id: number): Fighter | undefined {
  return id >= 0 ? world.fighters[id] : undefined;
}
function itemById(world: SimWorld, id: number): Item | undefined {
  return id >= 0 ? world.items[id] : undefined;
}

/** Sahnedeki "eşya birimi" sayısı (DESIGN §9 "en fazla 2"): ölü olmayanlar; hançer çifti tek birim (⌈dagger/2⌉). */
export function itemUnitsOnField(world: SimWorld): number {
  let units = 0, daggers = 0;
  for (let i = 0; i < world.items.length; i++) {
    const it = world.items[i]!;
    if (it.state === 'dead') continue;
    if (it.propId === 'dagger') daggers++; else units++;
  }
  return units + ((daggers + 1) >> 1);
}

function freeSlot(world: SimWorld): Item | undefined {
  for (let i = 0; i < world.items.length; i++) { const it = world.items[i]!; if (it.state === 'dead') return it; }
  if (world.items.length < MAX_ITEMS) { const it = createItem(world.items.length); world.items.push(it); return it; }   // sınırlı tahsis (≤ MAX_ITEMS kez)
  return undefined;
}

// ───────────────────────────── Doğma ─────────────────────────────
function initItem(it: Item, kind: SpawnableKind, propId: PropId, x: number, y: number, z: number, tick: number): void {
  it.kind = kind; it.propId = propId; it.state = 'ground';
  // vel.y ≠ 0 = "havada" işareti (yere inince sıfırlanır); doğuşta ilk yerçekimi adımı hemen uygulanır ki 3 u yukarıdaki eşya inmiş sayılmasın.
  v3set(it.pos, x, y, z); v3set(it.vel, 0, T.ITEMS.dropGravity * TICK_DT, 0); it.yaw = 0; it.spin = 0;
  it.holder = NO_ID; it.thrower = NO_ID; it.spawnedAt = tick;
  it.pickableAt = tick + T.ITEMS.dropCooldown;     // 3 u'dan düşüş 27 t; 0.5 s sonra alınabilir (düşürme kuralıyla aynı)
  it.despawnAt = tick + T.ITEMS.despawn;
  it.hits = 0; it.breakAt = NO_TICK; it.hitMask = 0;
  it.length = T.ITEMS.length[propId] ?? 0;         // katalog max.y + holder ofseti (kalkanlar 0)
  it.holderOffsetY = T.ITEMS.holderOffsetY[propId] ?? 0;
}

/** Ağırlıklı tür seçimi (DESIGN §9: 1H 30 / 2H 20 / dual 20 / asa 15 / kalkan 15). */
const KINDS: readonly SpawnableKind[] = ['1h', '2h', 'dual', 'staff', 'shield'];
function pickKind(world: SimWorld, tick: number): SpawnableKind {
  let total = 0;
  for (let i = 0; i < KINDS.length; i++) total += T.ITEMS.weights[KINDS[i]!];
  let r = hashNoise(world.seed, tick, SALT_KIND) * total;
  for (let i = 0; i < KINDS.length; i++) {
    const k = KINDS[i]!;
    r -= T.ITEMS.weights[k];
    if (r < 0) return k;
  }
  return KINDS[KINDS.length - 1]!;
}
function pickProp(world: SimWorld, tick: number, kind: SpawnableKind): PropId {
  const list = T.ITEMS.propsByKind[kind];
  const idx = Math.min(list.length - 1, Math.floor(hashNoise(world.seed, tick, SALT_PROP) * list.length));
  return list[idx] ?? 'sword_1handed';
}
/** Doğma noktası üstünde (yerde/uçan) eşya var mı. */
function pointOccupied(world: SimWorld, p: Vec3): boolean {
  for (let i = 0; i < world.items.length; i++) {
    const it = world.items[i]!;
    if (it.state === 'dead' || it.state === 'held') continue;
    if (hyp(it.pos.x - p.x, it.pos.z - p.z) < SPAWN_POINT_CLEAR_R) return true;
  }
  return false;
}

function spawn(world: SimWorld, kind: SpawnableKind, at: Vec3, tick: number): number {
  const propId = pickProp(world, tick, kind);
  const first = freeSlot(world);
  if (!first) return NO_ID;
  const y = at.y + T.ITEMS.dropHeight;
  if (kind === 'dual') {
    // İki hançer: sağ (+side) ve sol; ikisi de bağımsız Item — birlikte alınınca 'dual', tek alınınca '1h'.
    initItem(first, kind, propId, at.x + DUAL_SPAWN_SPREAD, y, at.z, tick);
    const second = freeSlot(world);
    if (second) initItem(second, kind, propId, at.x - DUAL_SPAWN_SPREAD, y, at.z, tick);
    else first.kind = '1h';   // havuz doluysa tek hançer '1h' olarak doğar
  } else {
    initItem(first, kind, propId, at.x, y, at.z, tick);
  }
  const e = world.events.push(Ev.ItemSpawn, tick, NO_ID);
  e.item = first.id; e.x = at.x; e.y = y; e.z = at.z;
  return first.id;
}

/** Zamanlayıcı (adım 8 başı): tick ≥ 480 ∧ her 720 t; sahnede < 2 birim; boş bir noktaya, 3 u yükseğe. */
function stepSpawnTimer(world: SimWorld, tick: number): void {
  if (world.match.phase === 'over') return;
  const since = tick - T.ITEMS.firstSpawn;
  if (since < 0 || since % T.ITEMS.interval !== 0) return;
  if (itemUnitsOnField(world) >= T.ITEMS.maxOnField) return;
  const pts = world.arena.itemSpawns;
  if (pts.length === 0) return;
  // Boş noktalar arasından hashNoise ile seç: k. boş nokta (tahsissiz iki geçiş).
  let free = 0;
  for (let i = 0; i < pts.length; i++) if (!pointOccupied(world, pts[i]!)) free++;
  if (free === 0) return;
  let want = Math.min(free - 1, Math.floor(hashNoise(world.seed, tick, SALT_POINT) * free));
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!;
    if (pointOccupied(world, p)) continue;
    if (want-- === 0) { spawn(world, pickKind(world, tick), p, tick); return; }
  }
}

// ───────────────────────────── Alma ─────────────────────────────
function canPickupState(s: FState): boolean {
  return s === FState.Idle || s === FState.Move || s === FState.Block || s === FState.Lying || s === FState.Crawl;
}
/** Bu dövüşçü bu eşyayı alabilir mi (DESIGN §9 / §2 B): elde silah varken yeni silah yok; 1H iken yalnız kalkan; kalkan varsa kalkan yok. */
function canTake(f: Fighter, it: Item): boolean {
  if (it.kind === 'shield') return !f.hasShield && (f.weaponItem === NO_ID || f.weapon === '1h');
  return f.weaponItem === NO_ID;
}
function pickable(it: Item, tick: number): boolean {
  return it.state === 'ground' && it.pickableAt <= tick && it.vel.y === 0;   // yere inmiş ∧ soğuma bitmiş
}
/** Eşya f'nin alma menzil/konisinde mi (≤ 1.0 u, aim ±80°; üst üsteyse koni aranmaz). */
function inPickupCone(f: Fighter, aimYaw: number, it: Item): boolean {
  const dx = it.pos.x - f.pos.x, dz = it.pos.z - f.pos.z;
  const d = hyp(dx, dz);
  if (d > T.ITEMS.pickupRange) return false;
  if (d < 1e-3) return true;
  const cosHalf = Math.cos(degToRad(T.ITEMS.pickupConeDeg));
  const dot = (dx * fwdX(aimYaw) + dz * fwdZ(aimYaw)) / d;
  return dot >= cosHalf;
}

function attachToFighter(world: SimWorld, f: Fighter, it: Item, tick: number, left: boolean): void {
  it.state = 'held'; it.holder = f.id; it.thrower = NO_ID; it.hitMask = 0;
  v3set(it.vel, 0, 0, 0); v3set(it.pos, f.pos.x, f.pos.y, f.pos.z);
  it.despawnAt = NO_TICK; it.pickableAt = NO_TICK;
  if (isWeaponKind(it.kind) && it.breakAt === NO_TICK) it.breakAt = tick + T.ITEMS.durabilitySec * TICK_HZ;   // dayanıklılık 30 s ilk almadan
  if (it.kind === 'shield') { f.shieldItem = it.id; f.hasShield = true; }
  else if (left) { f.weaponItemL = it.id; }
  else { f.weaponItem = it.id; }
  const e = world.events.push(Ev.ItemPickup, tick, f.id);
  e.item = it.id; e.v0 = T.ITEMS.equipTicks;
}

/** `maxRange` verilirse yalnız o mesafedeki eşya alınır (moves.ts "ayağının dibindeki silah önce gelir" kuralı). */
function tryPickup(world: SimWorld, f: Fighter, tick: number, maxRange = Infinity): boolean {
  if (!canPickupState(f.state)) return false;
  const inp = world.inputs[f.id];
  const aimYaw = inp && Number.isFinite(inp.aimYaw) ? inp.aimYaw : f.yaw;
  // En yakın alınabilir eşya (koni + menzil + kural). 1H + kalkan önceliği canTake'ten gelir: 1H'lıya yalnız kalkan aday.
  let best: Item | undefined; let bestD = Infinity;
  for (let i = 0; i < world.items.length; i++) {
    const it = world.items[i]!;
    if (!pickable(it, tick) || !canTake(f, it) || !inPickupCone(f, aimYaw, it)) continue;
    const d = hyp(it.pos.x - f.pos.x, it.pos.z - f.pos.z);
    if (d > maxRange) continue;
    if (d < bestD) { bestD = d; best = it; }
  }
  if (!best) return false;
  let second: Item | undefined;
  if (best.propId === 'dagger') {
    // Menzildeki ikinci hançer de alınır → 'dual' (DESIGN §9: dual = iki ayrı Item, sağ + sol).
    for (let i = 0; i < world.items.length; i++) {
      const it = world.items[i]!;
      if (it === best || it.propId !== 'dagger' || !pickable(it, tick) || !inPickupCone(f, aimYaw, it)) continue;
      second = it; break;
    }
  }
  if (best.kind === 'shield') {
    attachToFighter(world, f, best, tick, true);
  } else {
    const newKind: WeaponKind = second ? 'dual' : weaponKindOf(best.kind);
    // 2H / dual / asa alınınca kalkan düşer (DESIGN §4.2 / §9).
    if (f.hasShield && newKind !== '1h') dropShield(world, f, tick, Ev.ItemPickup);
    attachToFighter(world, f, best, tick, false);
    if (second) attachToFighter(world, f, second, tick, true);
    f.weapon = newKind;
  }
  // PickUp@0-0.65 tS 2.2 = 18 t; equip 14. t (view). Yatarken/sürünürken üst gövde — durum değişmez (ANIMATION §2.2).
  if (f.state !== FState.Lying && f.state !== FState.Crawl) {
    f.state = FState.PickingUp; f.stateTick = tick; f.stateUntil = tick + T.ITEMS.pickupTicks; f.blocking = false;
  }
  return true;
}

// ───────────────────────────── Düşürme ─────────────────────────────
/** Tek eşyayı elden yere bırakır: hız = taşıyıcı + yukarı 1.5–2.5 + geri 1.5–2.5 u/s (hashNoise), 0.5 s alınamaz, 20 s'de kaybolur. */
function releaseToGround(world: SimWorld, f: Fighter, it: Item, tick: number, reason: Ev, side: number): void {
  const up = lerp(T.ITEMS.dropUpMin, T.ITEMS.dropUpMax, hashNoise(world.seed, tick, it.id + SALT_DROP_UP));
  const back = lerp(T.ITEMS.dropBackMin, T.ITEMS.dropBackMax, hashNoise(world.seed, tick, it.id + SALT_DROP_BACK));
  const fx = fwdX(f.yaw), fz = fwdZ(f.yaw);
  v3set(it.pos, f.pos.x + leftX(f.yaw) * side, f.pos.y + DROP_ORIGIN_UP, f.pos.z + leftZ(f.yaw) * side);
  v3set(it.vel, f.vel.x - fx * back, Math.max(f.vel.y, 0) + up, f.vel.z - fz * back);
  it.state = 'ground'; it.holder = NO_ID; it.thrower = NO_ID; it.hitMask = 0; it.spin = 0;
  it.yaw = f.yaw;
  it.pickableAt = tick + T.ITEMS.dropCooldown;
  it.despawnAt = tick + T.ITEMS.despawn;
  const e = world.events.push(Ev.ItemDrop, tick, f.id);
  e.item = it.id; e.v0 = reason; e.x = it.pos.x; e.y = it.pos.y; e.z = it.pos.z;
}
function dropShield(world: SimWorld, f: Fighter, tick: number, reason: Ev): void {
  const it = itemById(world, f.shieldItem);
  f.shieldItem = NO_ID; f.hasShield = false;
  if (it && it.state === 'held' && it.holder === f.id) releaseToGround(world, f, it, tick, reason, DROP_ORIGIN_SIDE);
}
function dropWeapons(world: SimWorld, f: Fighter, tick: number, reason: Ev): void {
  const r = itemById(world, f.weaponItem);
  const l = itemById(world, f.weaponItemL);
  f.weaponItem = NO_ID; f.weaponItemL = NO_ID; f.weapon = 'unarmed';
  if (r && r.state === 'held' && r.holder === f.id) releaseToGround(world, f, r, tick, reason, -DROP_ORIGIN_SIDE * 0.5);
  if (l && l.state === 'held' && l.holder === f.id) releaseToGround(world, f, l, tick, reason, DROP_ORIGIN_SIDE * 0.5);
}
/** Kalkanın da düştüğü sebepler: guard break (DESIGN §4.2), ring-out/eleme/yeniden doğuş (beden sahneden çıkar). */
function shieldDropsFor(reason: Ev): boolean {
  return reason === Ev.GuardBreak || reason === Ev.Fell || reason === Ev.Eliminated || reason === Ev.Respawn;
}
/** Deterministik düşürme (DESIGN §9): knockdown / tek vuruşta stun ≥ 45 / tutulma / fırlatılma / ring-out → silah(lar) düşer (iki hançer
 *  birlikte); kalkan yalnız guard break ve sahneden çıkışta. Çağıran: hits.ts (Hit/Knockdown/Throw), grab.ts (Grab), block.ts (GuardBreak),
 *  match.ts (Fell/Eliminated). İdempotent: elde eşya yoksa hiçbir şey yapmaz. */
function drop(world: SimWorld, f: Fighter, tick: number, reason: Ev): void {
  if (reason !== Ev.GuardBreak) dropWeapons(world, f, tick, reason);
  if (shieldDropsFor(reason)) dropShield(world, f, tick, reason);
}

// ───────────────────────────── Fırlatma ─────────────────────────────
function throwWeapon(world: SimWorld, f: Fighter, dirX: number, dirZ: number, tick: number): void {
  if (f.state === FState.Throwing || f.state === FState.PickingUp) return;
  const it = itemById(world, f.weaponItem);
  if (!it || it.state !== 'held' || it.holder !== f.id) return;    // kalkan fırlatılamaz (weaponItem değil)
  let len = hyp(dirX, dirZ);
  if (len < 1e-4) { dirX = fwdX(f.yaw); dirZ = fwdZ(f.yaw); len = 1; }
  dirX /= len; dirZ /= len;
  f.yaw = yawOf(dirX, dirZ);
  // Bekleyen fırlatma: bırakma tick'i pickableAt'ta, bırakma hızı vel'de; 19. t'de step() serbest bırakır (holder durumu hâlâ Throwing ise).
  it.thrower = f.id;
  it.pickableAt = tick + T.ITEMS.throwReleaseTick;
  v3set(it.vel, dirX * T.ITEMS.throwSpeed, 0, dirZ * T.ITEMS.throwSpeed);
  it.yaw = f.yaw; it.spin = 0; it.hitMask = 0;
  f.state = FState.Throwing; f.stateTick = tick; f.stateUntil = tick + WEAPON_THROW_TICKS; f.blocking = false;
}
/** Bırakma anı: prop elden ayrılır, dövüşçü silahsız kalır (dual: sol hançer sağ ele geçer, sınıf '1h'). */
function releaseThrow(world: SimWorld, f: Fighter, it: Item, tick: number): void {
  const fx = fwdX(f.yaw), fz = fwdZ(f.yaw);
  v3set(it.pos, f.pos.x + fx * THROW_RELEASE_FWD, f.pos.y + THROW_RELEASE_UP, f.pos.z + fz * THROW_RELEASE_FWD);
  it.state = 'flying'; it.holder = NO_ID; it.hitMask = 0; it.spin = 0; it.pickableAt = NO_TICK; it.despawnAt = NO_TICK;
  if (f.weaponItem === it.id) {
    f.weaponItem = f.weaponItemL; f.weaponItemL = NO_ID;
    f.weapon = f.weaponItem === NO_ID ? 'unarmed' : '1h';
  } else if (f.weaponItemL === it.id) {
    f.weaponItemL = NO_ID; f.weapon = '1h';
  }
  const e = world.events.push(Ev.ItemThrow, tick, f.id);
  e.item = it.id; e.dx = fx; e.dz = fz; e.x = it.pos.x; e.y = it.pos.y; e.z = it.pos.z;
}

// ───────────────────────────── Dayanıklılık ─────────────────────────────
/** Eşyayı kırar (Ev.ItemBreak); eldeyse sahibinin alanları düzeltilir (dual'de kalan hançer '1h'). */
export function breakItem(world: SimWorld, it: Item, tick: number): void {
  if (it.state === 'dead') return;
  const f = fighterById(world, it.holder);
  if (f) {
    if (f.weaponItem === it.id) { f.weaponItem = f.weaponItemL; f.weaponItemL = NO_ID; f.weapon = f.weaponItem === NO_ID ? 'unarmed' : '1h'; }
    else if (f.weaponItemL === it.id) { f.weaponItemL = NO_ID; f.weapon = '1h'; }
    else if (f.shieldItem === it.id) { f.shieldItem = NO_ID; f.hasShield = false; }
  }
  const e = world.events.push(Ev.ItemBreak, tick, it.holder);
  e.item = it.id; e.x = it.pos.x; e.y = it.pos.y; e.z = it.pos.z;
  it.state = 'dead'; it.holder = NO_ID; it.thrower = NO_ID;
}
/** hits.ts (WP3) her silah isabetinde (blok dâhil) çağırır: 10. isabette kırılır. Yalnız `hits++` yapılsa da step() aynı eşiği uygular. */
export function noteWeaponHit(world: SimWorld, it: Item, tick: number): void {
  it.hits++;
  if (isWeaponKind(it.kind) && it.hits >= T.ITEMS.durabilityHits) breakItem(world, it, tick);
}

function despawn(world: SimWorld, it: Item, tick: number): void {
  const e = world.events.push(Ev.ItemDespawn, tick, NO_ID);
  e.item = it.id; e.x = it.pos.x; e.y = it.pos.y; e.z = it.pos.z;
  it.state = 'dead'; it.holder = NO_ID; it.thrower = NO_ID;
}

// ───────────────────────────── Uçan mermi isabeti ─────────────────────────────
/** Hurtbox kapalı/vurulamaz durumlar (ARCHITECTURE §4.4). */
function hurtboxOpen(f: Fighter, tick: number): boolean {
  const s = f.state;
  if (s === FState.Respawning || s === FState.Spawning || s === FState.Falling || s === FState.Eliminated) return false;
  if (f.invulnUntil > tick || f.spawnProtUntil > tick) return false;
  return true;
}
/** Küre (merkez c, yarıçap r) – dövüşçü kapsülü (dik: r 0.45, y ∈ [0.1, 2.2]; yatan: yatay, merkez y 0.35, ileri ±0.8). */
function sphereHitsFighter(c: Vec3, r: number, f: Fighter): boolean {
  let ax: number, ay: number, az: number, bx: number, by: number, bz: number;
  if (f.state === FState.Lying || f.state === FState.Crawl || f.state === FState.Knockdown) {
    const fx = fwdX(f.yaw), fz = fwdZ(f.yaw);
    ax = f.pos.x - fx * LYING_HURT_HALF; ay = f.pos.y + LYING_HURT_Y; az = f.pos.z - fz * LYING_HURT_HALF;
    bx = f.pos.x + fx * LYING_HURT_HALF; by = ay; bz = f.pos.z + fz * LYING_HURT_HALF;
  } else {
    ax = f.pos.x; ay = f.pos.y + HURT_Y0; az = f.pos.z;
    bx = f.pos.x; by = f.pos.y + HURT_Y1; bz = f.pos.z;
  }
  // segman üzerindeki en yakın nokta
  const ex = bx - ax, ey = by - ay, ez = bz - az;
  const l2 = ex * ex + ey * ey + ez * ez;
  let t = l2 > 0 ? ((c.x - ax) * ex + (c.y - ay) * ey + (c.z - az) * ez) / l2 : 0;
  t = clamp(t, 0, 1);
  const px = ax + ex * t - c.x, py = ay + ey * t - c.y, pz = az + ez * t - c.z;
  const rr = r + HURT_R;
  return px * px + py * py + pz * pz <= rr * rr;
}
function resolveFlyingHit(world: SimWorld, it: Item, tick: number): boolean {
  const r = is2H(it) ? T.ITEMS.throwRadius2H : T.ITEMS.throwRadius;
  for (let i = 0; i < world.fighters.length; i++) {
    const f = world.fighters[i]!;
    if (f.id === it.thrower || (it.hitMask & (1 << f.id)) !== 0 || !hurtboxOpen(f, tick)) continue;
    if (!sphereHitsFighter(it.pos, r, f)) continue;
    it.hitMask |= 1 << f.id;
    // yön = merminin yatay hızı (yoksa fırlatan → hedef)
    let dx = it.vel.x, dz = it.vel.z;
    let l = hyp(dx, dz);
    if (l < 1e-4) { const th = fighterById(world, it.thrower); dx = f.pos.x - (th ? th.pos.x : it.pos.x); dz = f.pos.z - (th ? th.pos.z : it.pos.z); l = hyp(dx, dz); }
    if (l < 1e-4) { dx = fwdX(it.yaw); dz = fwdZ(it.yaw); l = 1; }
    dx /= l; dz /= l;
    const dmg = is2H(it) ? T.ITEMS.throwDmg2H : T.ITEMS.throwDmg;
    const stun = is2H(it) ? T.ITEMS.throwStun2H : T.ITEMS.throwStun;
    const kb = T.ITEMS.throwKb, lift = T.ITEMS.throwLift;
    // Blok / parry / zırh / formül TEK yerde: hits.ts resolveCore (applyKnockback). Burada ön-uygulama YOK — eskiden guard −30 + çarpanlar
    // burada uygulanıp guardLastHitTick yazılınca resolveCore'un reblock (7 t) kuralı bloğu görmüyor, isabet bloksuz işleniyordu
    // (kbFloor 4.0, Hitstun, Ev.Hit). Guard maliyeti ApplyKnockbackFn'de parametre değil → hafif (−12); DESIGN §9 "ağır (−30)" için
    // ApplyKnockbackFn'e guardCost gerekir (needsWP0).
    world.systems.applyKnockback(world, f, dx, dz, kb, lift, dmg, stun, THROW_HITSTOP, it.thrower);
    const e = world.events.push(Ev.ItemHit, tick, it.thrower, f.id);
    e.item = it.id; e.v0 = kb; e.v1 = dmg; e.v2 = stun; e.dx = dx; e.dz = dz; e.x = it.pos.x; e.y = it.pos.y; e.z = it.pos.z;
    // ilk temas: dayanıklılık +1, kırılmadıysa yere düşer (herkes alabilir)
    it.hits++;
    if (isWeaponKind(it.kind) && it.hits >= T.ITEMS.durabilityHits) { breakItem(world, it, tick); return true; }
    v3set(it.vel, -dx * T.ITEMS.dropBackMin, T.ITEMS.dropUpMin, -dz * T.ITEMS.dropBackMin);
    it.state = 'ground'; it.spin = 0;
    it.pickableAt = tick + T.ITEMS.dropCooldown; it.despawnAt = tick + T.ITEMS.despawn;
    return true;
  }
  return false;
}

// ───────────────────────────── Fizik ─────────────────────────────
/** Havadaki eşya (yerde-düşen ya da uçan): yerçekimi, iniş (platform üstü), kill plane. Döner: bu tick indi mi. */
function integrateAirborne(world: SimWorld, it: Item, tick: number, gravity: number): boolean {
  const prevY = it.pos.y;
  it.vel.y += gravity * TICK_DT;
  it.pos.x += it.vel.x * TICK_DT; it.pos.y += it.vel.y * TICK_DT; it.pos.z += it.vel.z * TICK_DT;
  if (it.vel.y <= 0) {
    const g = world.query.groundY(it.pos.x, it.pos.z, prevY);
    if (Number.isFinite(g) && it.pos.y <= g) {
      it.pos.y = g; v3set(it.vel, 0, 0, 0);
      return true;
    }
  }
  if (it.pos.y < world.arena.killY) despawn(world, it, tick);   // kenardan düşen yok olur
  return false;
}

function stepItem(world: SimWorld, it: Item, tick: number): void {
  switch (it.state) {
    case 'ground': {
      if (it.vel.y !== 0 || it.vel.x !== 0 || it.vel.z !== 0) {
        integrateAirborne(world, it, tick, T.ITEMS.dropGravity);
        return;
      }
      it.yaw += T.ITEMS.groundYawRate * TICK_DT;        // yerde yavaş yaw (salınım view'da)
      if (it.despawnAt !== NO_TICK && tick >= it.despawnAt) despawn(world, it, tick);
      return;
    }
    case 'flying': {
      it.spin += T.ITEMS.throwSpin * Math.PI * 2 * TICK_DT;   // 3 tur/s (prop yerel X ekseni)
      const landed = integrateAirborne(world, it, tick, T.ITEMS.throwGravity);
      if (it.state !== 'flying') return;               // kill plane
      if (landed) {
        it.state = 'ground'; it.spin = 0; it.thrower = NO_ID;
        it.pickableAt = tick; it.despawnAt = tick + T.ITEMS.despawn;   // yere düşer, herkes alabilir
        return;
      }
      resolveFlyingHit(world, it, tick);
      return;
    }
    case 'held': {
      const f = fighterById(world, it.holder);
      if (!f) { despawn(world, it, tick); return; }
      v3set(it.pos, f.pos.x, f.pos.y, f.pos.z);        // bot algısı / yakınlık sorguları için sahibini izler
      // Bekleyen fırlatma
      if (it.thrower === f.id) {
        if (f.state !== FState.Throwing) { it.thrower = NO_ID; it.pickableAt = NO_TICK; v3set(it.vel, 0, 0, 0); }   // windup'ta kesildi
        else if (tick >= it.pickableAt) { releaseThrow(world, f, it, tick); return; }
      }
      // Dayanıklılık (10 isabet ∨ 30 s) — yalnız silahlar
      if (isWeaponKind(it.kind) && (it.hits >= T.ITEMS.durabilityHits || (it.breakAt !== NO_TICK && tick >= it.breakAt))) {
        breakItem(world, it, tick);
      }
      return;
    }
    default: return;
  }
}

/** Sahibinin durumu eşyanın elde kalamayacağı bir durumsa (defansif; hits/grab/match zaten drop() çağırır). */
function forcedDropReason(s: FState): Ev | -1 {
  switch (s) {
    case FState.Held: case FState.Carried: return Ev.Grab;
    case FState.Knockdown: case FState.Lying: return Ev.Knockdown;
    case FState.Falling: return Ev.Fell;
    case FState.Respawning: return Ev.Respawn;
    case FState.Eliminated: return Ev.Eliminated;
    default: return -1;
  }
}

function step(world: SimWorld, tick: number): void {
  stepSpawnTimer(world, tick);
  // Sahip durumu kontrolü + items.ts'nin sahiplendiği dövüşçü durumlarının bitişi (PickingUp 18 t; silah fırlatma Throwing 35 t)
  for (let i = 0; i < world.fighters.length; i++) {
    const f = world.fighters[i]!;
    if (f.weaponItem !== NO_ID || f.weaponItemL !== NO_ID || f.shieldItem !== NO_ID) {
      const reason = forcedDropReason(f.state);
      if (reason !== -1) drop(world, f, tick, reason);
    }
    // stateUntil = durumun bittiği ilk tick; geçiş bir önceki tick'in sonunda (adım 8) yapılır ki o tick'te adım 4 (moves) Idle görsün.
    if (f.state === FState.PickingUp && tick + 1 >= f.stateUntil) { f.state = FState.Idle; f.stateTick = tick + 1; }
    else if (f.state === FState.Throwing && f.grabTarget === NO_ID && tick + 1 >= f.stateUntil) { f.state = FState.Idle; f.stateTick = tick + 1; }
  }
  for (let i = 0; i < world.items.length; i++) stepItem(world, world.items[i]!, tick);
}

/** ItemApi uygulaması — WP9 `world.systems.items = ITEMS_API` (ya da createItemApi()) ile bağlar. */
export function createItemApi(): ItemApi {
  return { spawn, tryPickup, drop, throwWeapon, step };
}
export const ITEMS_API: ItemApi = createItemApi();

// Yardımcılar (bot/HUD): dövüşçünün silah uç menzili = eşya length + sınıf reach bonusu (DESIGN §4.2 "reach").
export function weaponLengthOf(world: SimWorld, f: Fighter): number {
  const it = itemById(world, f.weaponItem);
  return it && it.state === 'held' ? it.length : 0;
}
/** Bırakma noktasını hesaplar (view/F3: fırlatma önizlemesi); tahsissiz, out'a yazar. */
export function throwOrigin(out: Vec3, f: Fighter): Vec3 {
  return v3set(out, f.pos.x + fwdX(f.yaw) * THROW_RELEASE_FWD, f.pos.y + THROW_RELEASE_UP, f.pos.z + fwdZ(f.yaw) * THROW_RELEASE_FWD);
}
